'use client';

import { useState, type FormEvent } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Send, Check } from 'lucide-react';
import Reveal from './Reveal';

const styleOptions = [
  { id: 'minimaliste', fr: 'Minimaliste', en: 'Minimalist' },
  { id: 'ludique', fr: 'Ludique', en: 'Playful' },
  { id: 'professionnel', fr: 'Professionnel', en: 'Professional' },
  { id: 'rustique', fr: 'Rustique', en: 'Rustic' },
  { id: 'moderne', fr: 'Moderne', en: 'Modern' },
  { id: 'classique', fr: 'Classique', en: 'Classic' },
  { id: 'elegant', fr: 'Élégant', en: 'Elegant' },
  { id: 'creatif', fr: 'Créatif', en: 'Creative' },
  { id: 'technophile', fr: 'Technophile', en: 'Tech-savvy' },
];

const pageOptions = [
  { id: 'accueil', fr: 'Accueil', en: 'Home' },
  { id: 'a-propos', fr: 'À propos', en: 'About' },
  { id: 'services', fr: 'Services', en: 'Services' },
  { id: 'portfolio', fr: 'Portfolio / Réalisations', en: 'Portfolio / Work' },
  { id: 'blog', fr: 'Blog', en: 'Blog' },
  { id: 'contact', fr: 'Contact', en: 'Contact' },
  { id: 'faq', fr: 'FAQ', en: 'FAQ' },
  { id: 'tarifs', fr: 'Tarifs', en: 'Pricing' },
  { id: 'equipe', fr: 'Équipe', en: 'Team' },
  { id: 'temoignages', fr: 'Témoignages', en: 'Testimonials' },
  { id: 'boutique', fr: 'Boutique', en: 'Shop' },
  { id: 'mentions-legales', fr: 'Mentions légales', en: 'Legal notices' },
];

const heroOptions = [
  { id: 'image', fr: 'Image plein écran', en: 'Full-screen image' },
  { id: 'video', fr: 'Vidéo de fond', en: 'Background video' },
  { id: 'slider', fr: 'Slider / Carrousel', en: 'Slider / Carousel' },
  { id: 'animation', fr: 'Animation / Motion', en: 'Animation / Motion' },
  { id: 'text-only', fr: 'Texte sur couleur de fond', en: 'Text on background color' },
  { id: 'split', fr: 'Split (image + texte)', en: 'Split (image + text)' },
];

const domainOptions = [
  { id: 'owned', fr: 'Déjà acheté', en: 'Already owned' },
  { id: 'to-buy', fr: 'À acheter', en: 'To purchase' },
  { id: 'to-transfer', fr: 'À transférer', en: 'To transfer' },
];

const featureOptions = [
  { id: 'contact-form', fr: 'Formulaire de contact', en: 'Contact form' },
  { id: 'payment', fr: 'Paiement en ligne', en: 'Online payment' },
  { id: 'members', fr: 'Espace membre', en: 'Members area' },
  { id: 'booking', fr: 'Réservation / Calendrier', en: 'Booking / Calendar' },
  { id: 'newsletter', fr: 'Newsletter', en: 'Newsletter' },
  { id: 'social', fr: 'Réseaux sociaux', en: 'Social media links' },
  { id: 'testimonials', fr: 'Témoignages / Avis', en: 'Testimonials / Reviews' },
  { id: 'blog', fr: 'Blog', en: 'Blog' },
  { id: 'ecommerce', fr: 'E-commerce', en: 'E-commerce' },
];

const inputClass =
  'w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all';

const toggleBtnClass = (active: boolean) =>
  `px-3.5 py-2 rounded-lg border text-sm font-medium transition-all ${
    active
      ? 'bg-electric border-electric text-white'
      : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:border-white/[0.15] hover:text-white/70'
  }`;

const toggleIn = (list: string[], id: string) =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

export default function BriefForm() {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [heroType, setHeroType] = useState<string | null>(null);
  const [heroContentReady, setHeroContentReady] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<string | null>(null);
  const [needsEmail, setNeedsEmail] = useState<boolean | null>(null);
  const [multilingual, setMultilingual] = useState<boolean | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const val = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement)?.value || '';

    const data = {
      name: val('name'),
      email: val('email'),
      phone: val('phone'),
      company: val('company'),
      businessDescription: val('businessDescription'),
      objective: val('objective'),
      website: val('website'),
      multilingual,
      audience: val('audience'),
      audienceProfile: val('audienceProfile'),
      pages: selectedPages,
      subpages: val('subpages'),
      heroType,
      heroContentReady,
      heroHeadline: val('heroHeadline'),
      heroCta: val('heroCta'),
      heroCtaTarget: val('heroCtaTarget'),
      domainStatus,
      domainName: val('domainName'),
      needsEmail,
      styles: selectedStyles,
      emotions: val('emotions'),
      slogan: val('slogan'),
      inspiration: val('inspiration'),
      features: selectedFeatures,
      keywords: val('keywords'),
      notes: val('notes'),
    };

    try {
      const res = await fetch('/api/brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError(
        t(
          'Une erreur est survenue. Veuillez réessayer.',
          'Something went wrong. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Reveal amount={0}>
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-8 md:p-12 text-center max-w-2xl mx-auto">
          <div className="w-16 h-16 rounded-full bg-electric/10 border border-electric/20 flex items-center justify-center mx-auto mb-4">
            <Send className="w-7 h-7 text-electric" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('Brief envoyé !', 'Brief sent!')}
          </h3>
          <p className="text-white/50">
            {t(
              'Merci ! Nous analysons votre brief et revenons vers vous sous 24h.',
              "Thanks! We're reviewing your brief and will get back to you within 24 hours."
            )}
          </p>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal amount={0}>
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-8 md:p-10 space-y-8"
      >
        {/* ── Contact ──────────────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Vos coordonnées', 'Your contact info')}
          </legend>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white/60 mb-1.5">
                {t('Nom', 'Name')} *
              </label>
              <input
                type="text" id="name" name="name" required className={inputClass}
                placeholder={t('Votre nom', 'Your name')}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/60 mb-1.5">
                Email *
              </label>
              <input
                type="email" id="email" name="email" required className={inputClass}
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-white/60 mb-1.5">
                {t('Téléphone', 'Phone')}
              </label>
              <input
                type="tel" id="phone" name="phone" className={inputClass}
                placeholder={t('+33 6 12 34 56 78', '+1 (555) 000-0000')}
              />
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-white/60 mb-1.5">
                {t("Nom de l'entreprise", 'Company name')} *
              </label>
              <input
                type="text" id="company" name="company" required className={inputClass}
                placeholder={t('Votre entreprise', 'Your company')}
              />
            </div>
          </div>
        </fieldset>

        {/* ── Entreprise & projet ──────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Votre entreprise & projet', 'Your business & project')}
          </legend>

          <div>
            <label htmlFor="businessDescription" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Présentez votre entreprise / activité en quelques lignes', 'Describe your business / activity in a few lines')} *
            </label>
            <textarea
              id="businessDescription" name="businessDescription" rows={3} required
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Votre secteur, vos services, ce qui vous différencie...',
                'Your industry, services, what makes you different...'
              )}
            />
          </div>

          <div>
            <label htmlFor="objective" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quel est l\'objectif principal du site ?', 'What is the main objective of the website?')} *
            </label>
            <textarea
              id="objective" name="objective" rows={2} required
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Ex : générer des leads, vendre en ligne, informer, crédibiliser...',
                'e.g. generate leads, sell online, inform, build credibility...'
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-white/60 mb-1.5">
                {t('Site web actuel', 'Current website')}
              </label>
              <input
                type="text" id="website" name="website" className={inputClass}
                placeholder="www.example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">
                {t('Besoin de multilingue ?', 'Need multilingual?')}
              </label>
              <div className="flex gap-3">
                {[true, false].map((val) => (
                  <button
                    key={String(val)} type="button"
                    onClick={() => setMultilingual(val)}
                    className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      multilingual === val
                        ? 'bg-electric border-electric text-white'
                        : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:border-white/[0.15]'
                    }`}
                  >
                    {val ? t('Oui', 'Yes') : t('Non', 'No')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </fieldset>

        {/* ── Structure du site ────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Structure du site', 'Website structure')}
          </legend>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quelles pages souhaitez-vous ?', 'Which pages do you want?')}
            </label>
            <p className="text-xs text-white/30 mb-3">
              {t('Sélectionnez les pages prévues pour votre site', 'Select the pages planned for your website')}
            </p>
            <div className="flex flex-wrap gap-2">
              {pageOptions.map((p) => {
                const active = selectedPages.includes(p.id);
                return (
                  <button
                    key={p.id} type="button"
                    onClick={() => setSelectedPages((prev) => toggleIn(prev, p.id))}
                    className={toggleBtnClass(active)}
                  >
                    {active && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {t(p.fr, p.en)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="subpages" className="block text-sm font-medium text-white/60 mb-1.5">
              {t(
                'Avez-vous des sous-pages en tête ? Décrivez l\'arborescence souhaitée',
                'Do you have sub-pages in mind? Describe the desired site structure'
              )}
            </label>
            <textarea
              id="subpages" name="subpages" rows={3}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Ex : Services > Création de site, Services > SEO, À propos > Équipe...',
                'e.g. Services > Web design, Services > SEO, About > Team...'
              )}
            />
          </div>
        </fieldset>

        {/* ── Hero Header ────────────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            Hero Header
          </legend>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quel type de hero souhaitez-vous ?', 'What type of hero do you want?')}
            </label>
            <p className="text-xs text-white/30 mb-3">
              {t(
                'La grande section en haut de votre page d\'accueil',
                'The large section at the top of your homepage'
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {heroOptions.map((h) => {
                const active = heroType === h.id;
                return (
                  <button
                    key={h.id} type="button"
                    onClick={() => setHeroType(active ? null : h.id)}
                    className={toggleBtnClass(active)}
                  >
                    {active && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {t(h.fr, h.en)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              {t(
                'Avez-vous déjà le contenu visuel (photo, vidéo) pour le hero ?',
                'Do you already have visual content (photo, video) for the hero?'
              )}
            </label>
            <div className="flex gap-3">
              {[
                { val: 'yes', fr: 'Oui', en: 'Yes' },
                { val: 'partial', fr: 'En partie', en: 'Partially' },
                { val: 'no', fr: 'Non, à créer', en: 'No, to create' },
              ].map((opt) => (
                <button
                  key={opt.val} type="button"
                  onClick={() => setHeroContentReady(heroContentReady === opt.val ? null : opt.val)}
                  className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    heroContentReady === opt.val
                      ? 'bg-electric border-electric text-white'
                      : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:border-white/[0.15]'
                  }`}
                >
                  {t(opt.fr, opt.en)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="heroHeadline" className="block text-sm font-medium text-white/60 mb-1.5">
              {t(
                'Quel message principal afficher dans le hero ?',
                'What main message should the hero display?'
              )}
            </label>
            <input
              type="text" id="heroHeadline" name="heroHeadline" className={inputClass}
              placeholder={t(
                'Ex : Transformez vos idées en réalité digitale',
                'e.g. Turn your ideas into digital reality'
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="heroCta" className="block text-sm font-medium text-white/60 mb-1.5">
                {t('Texte du bouton principal (CTA)', 'Main button text (CTA)')}
              </label>
              <input
                type="text" id="heroCta" name="heroCta" className={inputClass}
                placeholder={t('Ex : Demander un devis', 'e.g. Get a quote')}
              />
            </div>
            <div>
              <label htmlFor="heroCtaTarget" className="block text-sm font-medium text-white/60 mb-1.5">
                {t('Où mène ce bouton ?', 'Where does this button lead?')}
              </label>
              <input
                type="text" id="heroCtaTarget" name="heroCtaTarget" className={inputClass}
                placeholder={t('Ex : Page contact, formulaire devis...', 'e.g. Contact page, quote form...')}
              />
            </div>
          </div>
        </fieldset>

        {/* ── Cible ────────────────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Votre cible', 'Your target')}
          </legend>

          <div>
            <label htmlFor="audience" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Qui sont vos clients / utilisateurs cibles ?', 'Who are your target clients / users?')}
            </label>
            <textarea
              id="audience" name="audience" rows={2}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Particuliers, entreprises, tranche d\'age, localisation...',
                'Individuals, businesses, age range, location...'
              )}
            />
          </div>

          <div>
            <label htmlFor="audienceProfile" className="block text-sm font-medium text-white/60 mb-1.5">
              {t(
                'Décrivez vos clients potentiels : profil, centres d\'intérêt, usage des technologies',
                'Describe your potential customers: profile, interests, technology usage'
              )}
            </label>
            <textarea
              id="audienceProfile" name="audienceProfile" rows={3}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Profil démographique, habitudes en ligne, réseaux sociaux utilisés...',
                'Demographics, online habits, social media used...'
              )}
            />
          </div>
        </fieldset>

        {/* ── Design & identité ────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Design & identité', 'Design & identity')}
          </legend>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quels styles / ambiances souhaitez-vous ?', 'What styles / vibes do you want?')} *
            </label>
            <p className="text-xs text-white/30 mb-3">
              {t('Sélectionnez un ou plusieurs styles', 'Select one or more styles')}
            </p>
            <div className="flex flex-wrap gap-2">
              {styleOptions.map((s) => {
                const active = selectedStyles.includes(s.id);
                return (
                  <button
                    key={s.id} type="button"
                    onClick={() => setSelectedStyles((prev) => toggleIn(prev, s.id))}
                    className={toggleBtnClass(active)}
                  >
                    {active && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {t(s.fr, s.en)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="emotions" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quelles émotions ou image voulez-vous transmettre ?', 'What emotions or image do you want to convey?')}
            </label>
            <textarea
              id="emotions" name="emotions" rows={2}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Confiance, modernité, proximité, luxe, dynamisme...',
                'Trust, modernity, proximity, luxury, energy...'
              )}
            />
          </div>

          <div>
            <label htmlFor="slogan" className="block text-sm font-medium text-white/60 mb-1.5">
              {t(
                'Avez-vous un slogan ou une devise à mettre en avant ?',
                'Do you have a slogan or motto to highlight?'
              )}
            </label>
            <input
              type="text" id="slogan" name="slogan" className={inputClass}
              placeholder={t('Votre slogan ou phrase d\'accroche', 'Your tagline or catchphrase')}
            />
          </div>

          <div>
            <label htmlFor="inspiration" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quels sites aimez-vous ? Pourquoi ?', 'What websites do you like? Why?')}
            </label>
            <textarea
              id="inspiration" name="inspiration" rows={3}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'URLs + ce qui vous plait (design, navigation, ambiance...)',
                'URLs + what you like about them (design, navigation, vibe...)'
              )}
            />
          </div>
        </fieldset>

        {/* ── Fonctionnalités ──────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Fonctionnalités & intégrations', 'Features & integrations')}
          </legend>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              {t('Quelles fonctionnalités sont nécessaires ?', 'What features do you need?')}
            </label>
            <div className="flex flex-wrap gap-2">
              {featureOptions.map((f) => {
                const active = selectedFeatures.includes(f.id);
                return (
                  <button
                    key={f.id} type="button"
                    onClick={() => setSelectedFeatures((prev) => toggleIn(prev, f.id))}
                    className={toggleBtnClass(active)}
                  >
                    {active && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {t(f.fr, f.en)}
                  </button>
                );
              })}
            </div>
          </div>
        </fieldset>

        {/* ── Nom de domaine & Email ─────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            {t('Nom de domaine & email', 'Domain name & email')}
          </legend>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              {t('Avez-vous déjà un nom de domaine ?', 'Do you already have a domain name?')}
            </label>
            <div className="flex gap-3">
              {domainOptions.map((opt) => (
                <button
                  key={opt.id} type="button"
                  onClick={() => setDomainStatus(domainStatus === opt.id ? null : opt.id)}
                  className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    domainStatus === opt.id
                      ? 'bg-electric border-electric text-white'
                      : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:border-white/[0.15]'
                  }`}
                >
                  {t(opt.fr, opt.en)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="domainName" className="block text-sm font-medium text-white/60 mb-1.5">
              {t(
                'Nom de domaine souhaité ou existant',
                'Desired or existing domain name'
              )}
            </label>
            <input
              type="text" id="domainName" name="domainName" className={inputClass}
              placeholder={t('Ex : monentreprise.fr', 'e.g. mybusiness.com')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              {t(
                'Avez-vous besoin d\'emails professionnels (@votreentreprise.com) ?',
                'Do you need professional emails (@yourbusiness.com)?'
              )}
            </label>
            <div className="flex gap-3">
              {[true, false].map((val) => (
                <button
                  key={String(val)} type="button"
                  onClick={() => setNeedsEmail(val)}
                  className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    needsEmail === val
                      ? 'bg-electric border-electric text-white'
                      : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:border-white/[0.15]'
                  }`}
                >
                  {val ? t('Oui', 'Yes') : t('Non', 'No')}
                </button>
              ))}
            </div>
          </div>
        </fieldset>

        {/* ── SEO ──────────────────────────────────── */}
        <fieldset className="space-y-5">
          <legend className="text-lg font-semibold text-white mb-1">
            SEO & Performance
          </legend>

          <div>
            <label htmlFor="keywords" className="block text-sm font-medium text-white/60 mb-1.5">
              {t('Quels mots-clés ciblez-vous ?', 'What keywords are you targeting?')}
            </label>
            <textarea
              id="keywords" name="keywords" rows={2}
              className={`${inputClass} resize-none`}
              placeholder={t(
                'Ex : coaching sportif Paris, restaurant italien Lyon...',
                'e.g. fitness coaching London, Italian restaurant NYC...'
              )}
            />
          </div>
        </fieldset>

        {/* ── Notes ────────────────────────────────── */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-white/60 mb-1.5">
            {t('Notes supplémentaires', 'Additional notes')}
          </label>
          <textarea
            id="notes" name="notes" rows={3}
            className={`${inputClass} resize-none`}
            placeholder={t(
              'Autre chose à nous communiquer ?',
              'Anything else you want to share?'
            )}
          />
        </div>

        {/* ── Error & submit ───────────────────────── */}
        {error && (
          <p className="text-sm text-red-400 bg-red-400/10 px-4 py-2.5 rounded-lg border border-red-400/20">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading || selectedStyles.length === 0}
          className="w-full px-6 py-3.5 text-base font-semibold text-white bg-electric rounded-xl hover:bg-electric-bright transition-all duration-300 shadow-[0_0_20px_rgba(79,142,255,0.2)] hover:shadow-[0_0_30px_rgba(79,142,255,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? t('Envoi en cours...', 'Sending...')
            : t('Envoyer le brief', 'Send brief')}
        </button>
      </form>
    </Reveal>
  );
}
